import AppIconButton from "../../../components/form/AppIconButton";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { AppButton } from "../../../components/form";
import { anomalyService } from "../../../services/anomalyService";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import type { AnomalyPluginDetectorStatus } from "../../../types/anomaly";

function stateColor(
  status: AnomalyPluginDetectorStatus
): "success" | "default" | "warning" | "error" | "info" {
  if (status.state === "INITIALIZING" || status.state === "INIT") return "info";
  if (status.state === "RUNNING") return "success";
  if (status.state === "STOPPED" || status.state === "DISABLED") return "default";
  if (status.state === "NOT_SYNCED") return "warning";
  if (status.state === "UNAVAILABLE") return "default";
  if (status.running) return "success";
  return "error";
}

function isInitializing(status: AnomalyPluginDetectorStatus | null | undefined): boolean {
  const s = status?.state?.toUpperCase();
  return s === "INITIALIZING" || s === "INIT";
}

function rowKey(row: AnomalyPluginDetectorStatus): string {
  return row.pluginDetectorId || row.definitionId || row.detectorName || row.definitionName || "row";
}

export default function PluginDetectorsPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [items, setItems] = useState<AnomalyPluginDetectorStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pluginAvailable, setPluginAvailable] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const backend = await anomalyService.backendStatus();
      setPluginAvailable(Boolean(backend.pluginAvailable));
      if (!backend.pluginAvailable) {
        setItems([]);
        return;
      }
      const list = await anomalyService.listPluginDetectors();
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // OpenSearch AD stays in INITIALIZING until ~40 consecutive buckets have data — poll while waiting.
  useEffect(() => {
    const hasInit = items.some((row) => isInitializing(row));
    if (!hasInit) return;
    const timer = window.setInterval(() => {
      void load();
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [items, load]);

  const notify = (title: string, content: string) => {
    window.dispatchEvent(
      new CustomEvent("showGlobalNotification", {
        detail: { color: "success", icon: "check", title, content },
      })
    );
  };

  const handleStart = async (row: AnomalyPluginDetectorStatus) => {
    const key = rowKey(row);
    setBusyKey(key);
    setError(null);
    try {
      if (row.pluginDetectorId) {
        await anomalyService.startOpenSearchDetector(row.pluginDetectorId);
      } else if (row.definitionId) {
        await anomalyService.startPluginDetector(row.definitionId);
      } else {
        return;
      }
      notify(t("anomaly.pluginDetectorStart"), t("anomaly.pluginDetectorStarted"));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const handleSync = async (row: AnomalyPluginDetectorStatus) => {
    if (!row.definitionId) return;
    setBusyKey(rowKey(row));
    setError(null);
    try {
      await anomalyService.syncPluginDetector(row.definitionId);
      notify(t("anomaly.pluginDetectorSync"), t("anomaly.pluginDetectorSynced"));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const handleStop = async (row: AnomalyPluginDetectorStatus) => {
    if (!row.pluginDetectorId) return;
    setBusyKey(rowKey(row));
    setError(null);
    try {
      await anomalyService.stopOpenSearchDetector(row.pluginDetectorId);
      notify(t("anomaly.pluginDetectorStop"), t("anomaly.pluginDetectorStopped"));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const handleDeleteDetector = async (row: AnomalyPluginDetectorStatus) => {
    const name = row.detectorName || row.definitionName || row.pluginDetectorId || "";
    try {
      await confirmDelete({
        itemName: name,
        titleKey: "anomaly.pluginDetectorDelete",
        descriptionKey: "anomaly.pluginDetectorDeleteConfirm",
      });
    } catch (err) {
      if (isCancelled(err)) return;
      throw err;
    }
    setBusyKey(rowKey(row));
    setError(null);
    try {
      if (row.pluginDetectorId) {
        await anomalyService.deleteOpenSearchDetector(row.pluginDetectorId);
      } else if (row.definitionId) {
        await anomalyService.deletePluginDetector(row.definitionId);
      } else {
        return;
      }
      notify(t("anomaly.pluginDetectorDelete"), t("anomaly.pluginDetectorDeleted"));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  if (!pluginAvailable && !loading) {
    return (
      <Alert severity="info">
        {t("anomaly.pluginDetectorsUnavailable")}
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="h6">{t("anomaly.pluginDetectorsTitle")}</Typography>
        <Box flex={1} />
        <AppButton
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<RefreshRoundedIcon />}
          onClick={() => void load()}
          disabled={loading}
        >
          {t("common.refresh", { defaultValue: "Refresh" })}
        </AppButton>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        {t("anomaly.pluginDetectorsHelp")}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      {items.some((row) => isInitializing(row)) && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {t("anomaly.pluginDetectorInitializingHelp")}
        </Alert>
      )}
      {loading ? (
        <CircularProgress size={24} />
      ) : items.length === 0 ? (
        <Alert severity="info">{t("anomaly.pluginDetectorsEmpty")}</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("anomaly.pluginDetectorName")}</TableCell>
              <TableCell>{t("anomaly.definition")}</TableCell>
              <TableCell>{t("anomaly.pluginDetectorId")}</TableCell>
              <TableCell>{t("anomaly.pluginDetectorState")}</TableCell>
              <TableCell align="right">{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => {
              const key = rowKey(row);
              const busy = busyKey === key;
              const canStartStop = Boolean(row.pluginCapable && row.pluginDetectorId);
              const canSync = Boolean(row.definitionId && row.pluginCapable);
              return (
                <TableRow key={key}>
                  <TableCell>
                    <Typography variant="body2">
                      {row.detectorName || row.definitionName || "—"}
                    </Typography>
                    {!row.openSearchNative && (
                      <Typography variant="caption" color="text.secondary">
                        {t("anomaly.pluginDetectorPendingSync")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.definitionName || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {row.pluginDetectorId || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={row.detail || ""}>
                      <Chip size="small" color={stateColor(row)} label={row.state || "UNKNOWN"} />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t("anomaly.pluginDetectorSync")}>
                        <span>
                          <AppIconButton
                            size="small"
                            color="info"
                            disabled={busy || !canSync}
                            onClick={() => void handleSync(row)}
                          >
                            <SyncOutlinedIcon fontSize="small" />
                          </AppIconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("anomaly.pluginDetectorStart")}>
                        <span>
                          <AppIconButton
                            size="small"
                            color="success"
                            disabled={busy || !canStartStop || Boolean(row.running)}
                            onClick={() => void handleStart(row)}
                          >
                            <PlayArrowOutlinedIcon fontSize="small" />
                          </AppIconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("anomaly.pluginDetectorStop")}>
                        <span>
                          <AppIconButton
                            size="small"
                            color="warning"
                            disabled={busy || !canStartStop || !row.running}
                            onClick={() => void handleStop(row)}
                          >
                            <StopOutlinedIcon fontSize="small" />
                          </AppIconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("anomaly.pluginDetectorDelete")}>
                        <span>
                          <AppIconButton
                            size="small"
                            color="error"
                            disabled={busy || !row.pluginDetectorId}
                            onClick={() => void handleDeleteDetector(row)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </AppIconButton>
                        </span>
                      </Tooltip>
                      {row.definitionId && (
                        <Tooltip title={t("anomaly.edit")}>
                          <AppIconButton
                            size="small"
                            onClick={() => navigate(`/anomalies/${row.definitionId}/edit`)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </AppIconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
